import React from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal.jsx';
import { getAuthInstance, trackEvent } from '../firebase/config.js';
import { Events } from '../firebase/events.js';
import { CLINICALS_PLANS_MONTHLY, CLINICALS_PLANS_YEARLY, CLINICALS_CREDIT_PACKS } from '../data/clinicalPlans.js';
import PromoBanner from './PromoBanner.jsx';

const PlanChooserModal = ({ onClose, onChoosePlan, onBuyPack, entitlements, onManage, showCreditPacks, onRequireLogin, reason }) => {
    const navigate = useNavigate();
    const creditPacks = CLINICALS_CREDIT_PACKS;

    const [activeTab, setActiveTab] = React.useState('monthly');

    // Fires once when the paywall is shown, and once more on close — but only as a
    // "dismissed" (no selection made) if the close happens without ever picking a plan,
    // pack, or requiring login first. Those are forward progress, not abandonment, even
    // though they also unmount this component the same way a backdrop/X close does.
    const pickedRef = React.useRef(false);
    React.useEffect(() => {
        trackEvent(Events.paywall_view, { reason: reason || null, isClinicals: true, showCreditPacks: !!showCreditPacks });
        return () => {
            if (!pickedRef.current) {
                trackEvent(Events.paywall_dismissed, { reason: reason || null, isClinicals: true });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const markPicked = () => { pickedRef.current = true; };

    const currentTier = entitlements?.tier;
    const currentInterval = entitlements?.interval;
    const clinicalsTiers = ['class_prep','board_review','specialty_boards'];
    const baseTiers = ['basic','pro','elite'];
    const hasSubscription = currentTier && (clinicalsTiers.includes(currentTier) || baseTiers.includes(currentTier));
    // Determine guest status using Firebase auth (more reliable than entitlements uid)
    let isGuest = true;
    try {
        const auth = getAuthInstance();
        isGuest = !(auth && auth.currentUser && !auth.currentUser.isAnonymous);
    } catch (_) {
        // Fallback to entitlements heuristic if auth instance unavailable
        isGuest = !(entitlements?.uid && entitlements.uid !== 'anonymous');
    }
    const managePlanId = hasSubscription ? `${currentTier}_${currentInterval === 'year' ? 'yearly' : 'monthly'}` : null;

    const handleManageSubscription = () => {
        markPicked();
        if (onManage) {
            onManage();
            return;
        }
        if (managePlanId && onChoosePlan) {
            onChoosePlan(managePlanId);
        }
    };

    const activePlans = activeTab === 'monthly' ? CLINICALS_PLANS_MONTHLY : CLINICALS_PLANS_YEARLY;

    return (
        <Modal title="FlashFonic Clinicals Plans" onClose={onClose} size="3xl">
            <div className="mb-5 text-center">
                <p className="text-xs text-slate-400">1 credit = 1 question or flashcard generated. Credits refresh monthly.</p>
            </div>
            <PromoBanner className="mb-5" />
            {reason && (
                <div className="mb-5 rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 text-center">
                    {reason}
                </div>
            )}
            {/* Context + account banner */}
            {hasSubscription && (
                <div className="mb-6 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-slate-200">
                    <div>
                        You are currently on <strong className="capitalize">{(currentTier || '').replace(/_/g, ' ')}</strong> {currentInterval === 'year' ? '(yearly)' : '(monthly)'}
                    </div>
                    <button onClick={handleManageSubscription} className="rounded-xl px-3.5 py-2 text-sm font-semibold text-white hover:opacity-90 transition bg-gradient-to-r from-teal-600 to-emerald-600">Manage subscription</button>
                </div>
            )}

            {/* Subtitle */}
            <div className="mb-6 text-center">
                <p className="text-sm text-slate-400">Simple plans. Instant access. <span className="text-teal-300 font-medium">Cancel anytime.</span></p>
            </div>

            {/* Billing Toggle (slider) */}
            <div className="mb-7 flex items-center justify-center gap-3">
                <span className="text-xs text-slate-400">Monthly</span>
                <button
                    type="button"
                    aria-pressed={activeTab === 'yearly'}
                    onClick={() => setActiveTab(activeTab === 'monthly' ? 'yearly' : 'monthly')}
                    className="relative inline-flex h-6 w-12 items-center rounded-full bg-slate-800 ring-1 ring-white/10 px-1 transition-colors"
                >
                    <span
                        className={`h-5 w-5 transform rounded-full bg-teal-400 shadow-sm transition-transform ${activeTab === 'yearly' ? 'translate-x-6' : 'translate-x-0'}`}
                    />
                </button>
                <span className="flex items-center text-xs text-slate-400">
                    Yearly
                    <span className="ml-2 rounded bg-teal-500/15 px-2 py-0.5 text-[10px] font-medium text-teal-300">2 months free</span>
                </span>
            </div>

            {/* Plans grid */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                {activePlans.map(plan => {
                    const currentTierVal = entitlements?.tier || 'free';
                    const planTier = plan.id.replace(/_monthly|_yearly/, '');
                    const isOwned = (clinicalsTiers.includes(currentTierVal) || baseTiers.includes(currentTierVal)) && currentTierVal === planTier;
                    return (
                        <div
                            key={plan.id}
                            className={`group relative rounded-2xl border p-6 transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.02] ${plan.popular ? 'mt-7 border-2 shadow-xl' : ''}`}
                            style={{
                                background: plan.popular ? `linear-gradient(135deg, ${plan.color}26, ${plan.color}14)` : 'linear-gradient(135deg, rgba(2,6,23,0.6), rgba(2,6,23,0.85))',
                                borderColor: plan.popular ? plan.color : 'rgba(255,255,255,0.1)'
                            }}
                            onClick={() => {
                                markPicked();
                                if (isGuest) { if (typeof onRequireLogin === 'function') onRequireLogin(); return; }
                                if (!isOwned) onChoosePlan(plan.id);
                            }}
                        >
                            {plan.popular && (
                                <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-teal-500 px-3 py-1 text-xs font-semibold text-white shadow-lg shadow-teal-500/30">Most Popular</span>
                            )}
                            <div className="mb-4 text-center">
                                <h3 className="text-xl font-bold" style={{ color: plan.color }}>{plan.name}</h3>
                                <div className="mt-1 flex items-end justify-center gap-2">
                                    <span className="text-3xl font-bold text-white">{plan.price}</span>
                                    <span className="mb-1 text-base text-slate-400">{plan.period}</span>
                                </div>
                                {plan.originalPrice && (
                                    <div className="mt-1">
                                        <span className="text-sm text-slate-400 line-through">{plan.originalPrice}</span>
                                        <span className="ml-2 text-sm font-semibold text-emerald-400">{plan.savings}</span>
                                    </div>
                                )}
                            </div>
                            <ul className="mb-3 space-y-2 text-sm text-slate-200">
                                {plan.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-center gap-2">
                                        <span className="font-bold" style={{ color: plan.color }}>✓</span>
                                        {feature}
                                    </li>
                                ))}
                            </ul>
                            {plan.note && (
                                <p className="mb-5 text-[10px] text-slate-500 leading-relaxed">{plan.note}</p>
                            )}
                            {hasSubscription ? (
                                <button
                                    disabled={isOwned}
                                    className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition ${isOwned ? 'cursor-not-allowed bg-white/15' : ''}`}
                                    style={{ background: isOwned ? undefined : (plan.popular ? plan.color : `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`) }}
                                    onClick={(e) => { e.stopPropagation(); markPicked(); if (!isOwned) onChoosePlan(plan.id); }}
                                >
                                    {isOwned ? 'Current Plan' : 'Change Plan'}
                                </button>
                            ) : (
                                <button
                                    className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition"
                                    style={{ background: plan.popular ? plan.color : `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)` }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        markPicked();
                                        if (isGuest) { if (typeof onRequireLogin === 'function') onRequireLogin(); return; }
                                        onChoosePlan(plan.id);
                                    }}
                                >
                                    Get Started
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Credit packs visible only for paid tiers */}
            {showCreditPacks && hasSubscription && (
                <div className="mt-8 border-t border-white/10 pt-6 text-center">
                    <h3 className="text-xl font-semibold text-white">Need more credits?</h3>
                    <p className="mt-1 text-slate-400">One-time packs for subscribers · <span className="text-[12px] text-slate-400/90">1 credit = 1 question or flashcard generated</span></p>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                        {creditPacks.map(pack => (
                            <button
                                key={pack.id}
                                className="relative rounded-xl border border-white/10 bg-white/5 p-5 text-left transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.01]"
                                onClick={() => { markPicked(); if (isGuest) { if (typeof onRequireLogin === 'function') onRequireLogin(); return; } onBuyPack(pack.id); }}
                                style={{ boxShadow: pack.popular ? `0 12px 24px -6px ${pack.color}40` : undefined }}
                            >
                                {pack.popular && (
                                    <span className="absolute -top-2 right-3 rounded-md px-2 py-1 text-[10px] font-semibold text-white" style={{ background: pack.color }}>BEST VALUE</span>
                                )}
                                <div className="text-center">
                                    <div className="text-2xl font-bold" style={{ color: pack.color }}>{pack.amount}</div>
                                    <div className="mb-1 text-sm text-slate-400">credits</div>
                                    <div className="text-lg font-semibold text-white">{pack.price}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-8 flex items-center justify-center gap-4 text-xs text-slate-500">
                <button type="button" onClick={() => { markPicked(); onClose(); navigate('/terms'); }} className="hover:text-slate-300 hover:underline">Terms</button>
                <span className="text-slate-700">·</span>
                <button type="button" onClick={() => { markPicked(); onClose(); navigate('/privacy'); }} className="hover:text-slate-300 hover:underline">Privacy</button>
            </div>
        </Modal>
    );
};

export default PlanChooserModal;